import { CoreConfig } from '@fanduel/cpe-um-bundle/core-config/library';
import { CoreDeeplinks } from '@fanduel/cpe-um-bundle/core-deeplinks/library';
import { Account } from '@fanduel/cpe-um-bundle/account/library';
import { ModalPresenter } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import { CoreApiIdentities } from '@fanduel/cpe-um-bundle/core-api-identities/library';
import { CoreWebViewPluginRegistry } from '@fanduel/cpe-um-bundle/core-webview/library';
import { Logger } from './utils/logger';
import { Wallet } from '@fanduel/cpe-um-bundle/wallet/library';
import { AccountHub } from '@fanduel/cpe-um-bundle/account-hub/library';
import { CoreEvents } from '@fanduel/cpe-um-bundle/core-events/library';
import type { Event } from '@fanduel/cpe-um-bundle/core-events/contract';
import { getConfig } from '@/utils/config/config';
import { ResponsibleGaming } from '@fanduel/cpe-um-bundle/responsible-gaming/library';
import { SnowplowUm } from '@fanduel/snowplow-um';
import type { SnowplowConfig } from '@/tracking/snowplow/snowplow.types';
import { isAndroidWrapper } from '@/logic/platform';
import { CorePx } from '@fanduel/cpe-um-bundle/core-px/library';
import { TransactionHistoryLinkBlockPlugin } from '@/app/components/transaction-history/transaction-history-link-block-plugin';
import { initializeCasinoConfig, refreshCasinoConfig } from '@/logic/casino-config';
import { getUMConfig } from '@/utils/unified-modules/unified-modules';
import { ddRumAction, ddRumError } from '@/utils/datadog';
import { shouldUseWebRealityCheck } from '@/logic/reality-check/should-use-web-reality-check';
import { useStore } from '@/store/store';

let initialized: boolean = false;
let initializingPromise: Promise<boolean> | null = null;

const logger = new Logger();
const umLogger = new Logger(' ', '#fff', false);

type ConfigProps = {
    services: {
        snowplow: SnowplowConfig;
    };
};

/**
 * Defensive wrapper around UM `.initialize()` — silently no-ops if the module
 * is already registered in CoreIoC. UM singletons throw "already initialized
 * and registered" on double-init; this guards against:
 *   - React Strict Mode double-invocation
 *   - Hot module reload re-running init paths
 *   - Embedded consumers (e.g. rewards-hub-client) racing past their IoC check
 *
 * Re-throws anything other than "already initialized" so real failures surface.
 */
const safeInit = async (fn: () => void | Promise<void>) => {
    try {
        await fn();
    } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        if (!msg.includes('already initialized')) throw error;
    }
};

const initializeSnowplow = async (config: ConfigProps) => {
    try {
        SnowplowUm.initialize();
    } catch (error) {
        console.error('Error initializing Snowplow UM:', error);
        return;
    }

    const snowplowConfig: SnowplowConfig = config?.services?.snowplow;

    if (snowplowConfig) {
        const appId = isAndroidWrapper() ? snowplowConfig.androidAppId : snowplowConfig.appId;
        try {
            SnowplowUm.postInitialize({ ...snowplowConfig, appId });

            const userId = useStore.getState().userContext.accountSettings.accountID;
            if (userId) {
                SnowplowUm.setUserId(userId);
            }
        } catch (error) {
            console.error('Error initializing Snowplow UM:', error);
        }
    }
};

const handleCoreEvent = (event: Event) => {
    const prefix = `[${event.vendorTag}] [${event.name}]`;

    if (!event.data) {
        umLogger.debug(prefix);
        return;
    }

    const method = umLogger[event.logLevel as keyof Logger] ?? umLogger.debug;
    try {
        const json = JSON.parse(event.data);

        method(`${prefix} ${json.log.message}`);
    } catch {
        method(`${prefix} ${event.data}`);
    }
};

const initModules = async () => {
    if (initialized) {
        logger.warn('Unified modules already initialized.');
        return;
    }

    CoreWebViewPluginRegistry.add({
        id: 'show-header',
        onUrlLoaded: (ref) => {
            ref.updateConfig((conf) => ({
                ...conf,
                showHeader: true,
                showFooter: true
            }));
        }
    });

    CoreWebViewPluginRegistry.add(TransactionHistoryLinkBlockPlugin);

    // Build the CoreConfig payload up front — it is the single source of truth
    // for environment/region/clientId consumed by both CasinoConfig and the
    // legacy CoreConfig payload used by Account and the other existing UMs.
    const conf = await getUMConfig();
    if (!conf?.appDomain?.region) {
        ddRumError({
            name: 'MISSING_REGION',
            description: 'Missing appDomain region when initalizing UMs',
            use_case: 'GLOBAL'
        });
        location.reload();
        return;
    }

    await initializeCasinoConfig(conf);

    await CoreConfig.setConfig(conf);
    await safeInit(() => CoreEvents.initialize());

    const config = getConfig();
    if (config.log.logUnifiedModulesEvents) {
        CoreEvents.subscribe(handleCoreEvent);
    }

    await safeInit(() => CoreApiIdentities.initialize());
    await safeInit(() => ModalPresenter.initialize());

    await safeInit(() => CorePx.initialize());
    await safeInit(() => CoreDeeplinks.initialize());
    await safeInit(() => Account.initialize());
    await safeInit(() => Wallet.initialize());
    await safeInit(() => AccountHub.initialize());
    await safeInit(() => ResponsibleGaming.initialize());
    await initializeSnowplow(config);

    if (shouldUseWebRealityCheck()) {
        ResponsibleGaming.startRealityCheck();
    }

    initialized = true;
};

export const refreshConfig = async () => {
    const conf = await getUMConfig();
    await refreshCasinoConfig(conf);
    await CoreConfig.setConfig(conf);
};

export const initializeUnifiedModules = async () => {
    if (initialized) {
        return initialized;
    }

    if (initializingPromise) {
        await initializingPromise;
        return initialized;
    }

    initializingPromise = new Promise((resolve, reject) => {
        const startTime = performance.now();
        logger.log('Started initalizing unified modules...');
        initModules()
            .then(() => {
                logger.log('Unified modules initialized successfully.');
                ddRumAction({
                    name: 'UM_INITIALIZATION_FINISHED',
                    description: 'UM Bundle initialisation is done',
                    use_case: 'GLOBAL',
                    context: {
                        duration: performance.now() - startTime
                    }
                });
                resolve(initialized);
            })
            .catch((error) => {
                ddRumError({
                    name: 'UM_INITIALIZATION_ERROR',
                    description: 'UM Bundle initialisation failed',
                    use_case: 'GLOBAL',
                    context: {
                        message: error.message
                    }
                });
                logger.error('Error initializing unified modules:', error);
                reject(error);
            })
            .finally(() => {
                initializingPromise = null; // Reset the promise after initialization
            });
    });

    return initializingPromise;
};
